import './Stage1.css';
import './Life Meters/lifeMeter.css';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Physics, RigidBody } from '@react-three/rapier';
import { Canvas } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';

import AnimatedLights from './AnimatedLights.jsx';
import Blocks from './Blocks';
import VideoSky from './VideoSky';
import Background360Night from './Background360Night.jsx';
import S1LifeMeter from './S1LifeMeter.jsx';
import LevelCamera from './LevelCamera.jsx';
import SlideDown from './SlideDown.jsx';

export default function Stage1() {
  const navigate = useNavigate();
  const groupRef = useRef();

  const [transitionClass, setTransitionClass] = useState('in');
  const [phase, setPhase] = useState('play'); // 'play' | 'video'

  const cribModel = useGLTF('../stageAssets/stage1/models/crib-OPTOMIZED.glb');
  const babyMonitor = useGLTF('../stageAssets/stage1/models/baby_monitor.gltf');

  const panoMp4 = '/stageAssets/stage1/360 room/livingroom-360.mp4';
  const DAY_360 = "/stageAssets/stage1/360 room/staticBackground_DAY.jpg";
  const NIGHT_360 = "/stageAssets/stage1/360 room/staticBackground_NIGHT.jpg";

  const [panCommand, setPanCommand] = useState(null);
  const [lookAtCommand, setLookAtCommand] = useState(null);
  const [slideStart, setSlideStart] = useState(false);

  const [score, setScore] = useState(0);
  const [arrowPosition, setArrowPosition] = useState(0);
  const [isSleepSwitchOn, setIsSleepSwitchOn] = useState(true);

  // Sounds
  const crySounds = [
    '../stageAssets/stage1/baby-sounds/cry-small.mp3',
    '../stageAssets/stage1/baby-sounds/cry-small2.mp3',
    '../stageAssets/stage1/baby-sounds/cry-mid.mp3',
    '../stageAssets/stage1/baby-sounds/cry-long.mp3'
  ];

  const laughSounds = [
    '../stageAssets/stage1/baby-sounds/laugh-small.mp3',
    '../stageAssets/stage1/baby-sounds/laugh-mid1.mp3',
    '../stageAssets/stage1/baby-sounds/laugh-mid2.mp3',
    '../stageAssets/stage1/baby-sounds/laugh-mid1.mp3'
  ];

  const babbleSounds = [
    '../stageAssets/stage1/baby-sounds/babble1.mp3',
    '../stageAssets/stage1/baby-sounds/babble2.mp3',
    '../stageAssets/stage1/baby-sounds/babble-daddy.mp3',
    '../stageAssets/stage1/baby-sounds/babble-mama.mp3'
  ];

  const [crySoundIndex, setCrySoundIndex] = useState(0);
  const [laughSoundIndex, setLaughSoundIndex] = useState(0);
  const [babbleSoundIndex, setBabbleSoundIndex] = useState(0);

  // Wheel slices (same as your file)
  const redSlices = useMemo(() => ([
    [320, 360],
    [200, 239],
    [80, 119]
  ]), []);

  const yellowSlices = useMemo(() => ([
    [280, 319],
    [160, 199],
    [0, 39]
  ]), []);

  const blueSlices = useMemo(() => ([
    [240, 279],
    [120, 159],
    [40, 79]
  ]), []);

  // --- helpers to get color + wedgeTier from the spinner ---
  const segments = useMemo(() => ([
    { start: 0, end: 39, color: 'yellow' },
    { start: 40, end: 79, color: 'blue' },
    { start: 80, end: 119, color: 'red' },
    { start: 120, end: 159, color: 'blue' },
    { start: 160, end: 199, color: 'yellow' },
    { start: 200, end: 239, color: 'red' },
    { start: 240, end: 279, color: 'blue' },
    { start: 280, end: 319, color: 'yellow' },
    { start: 320, end: 360, color: 'red' },
  ]), []);

  function findSegmentIndex(angle) {
    // normalize
    let a = angle % 360;
    if (a < 0) a += 360;

    // treat 360 as 360 (your slice includes 360)
    if (a === 0 && angle === 360) a = 360;

    for (let i = 0; i < segments.length; i++) {
      const s = segments[i];
      if (a >= s.start && a <= s.end) return i;
    }
    // fallback
    return 0;
  }

  function computeWedgeTier(angle) {
    const idx = findSegmentIndex(angle);
    const current = segments[idx];

    // if in blue, tier doesn't matter much (night forces Y->0)
    if (current.color === 'blue') return { isNight: true, wedgeTier: 2 };

    // count how many non-blue wedges remain until next blue
    let count = 0;
    for (let step = 0; step < segments.length; step++) {
      const j = (idx + step) % segments.length;
      const c = segments[j].color;
      if (c === 'blue') break;
      count++;
    }

    // clamp to 1..3 (your wheel produces 1,2,3)
    const tier = Math.max(1, Math.min(3, count));
    return { isNight: false, wedgeTier: tier };
  }

  const { isNight: isInBlueSlice, wedgeTier } = useMemo(
    () => computeWedgeTier(arrowPosition),
    [arrowPosition, segments]
  );

  // Navigate
  const handleNavigate = () => {
    setTransitionClass('out');
    navigate("/Stage2");
  };

  // phase video timers
  useEffect(() => {
    if (phase !== 'video') return;

    const slideTimer = setTimeout(() => setSlideStart(true), 7000);
    const navigateTimer = setTimeout(() => handleNavigate(), 22000);

    return () => {
      clearTimeout(slideTimer);
      clearTimeout(navigateTimer);
    };
  }, [phase]);

  // click logic
  const handleBlockClick = useCallback((color) => {
    const isInRedSlice = redSlices.some(([start, end]) => arrowPosition >= start && arrowPosition <= end);
    const isInYellowSlice = yellowSlices.some(([start, end]) => arrowPosition >= start && arrowPosition <= end);
    const isInBlueSliceLocal = blueSlices.some(([start, end]) => arrowPosition >= start && arrowPosition <= end);

    if (color === 'red' && isInRedSlice) {
      setScore(prev => prev + 1);
      setIsSleepSwitchOn(true);
    } else if (color === 'yellow' && isInYellowSlice) {
      setScore(prev => prev + 1);
      setIsSleepSwitchOn(true);
    } else if (color === 'blue' && isInBlueSliceLocal) {
      setScore(prev => prev - 1);
      setIsSleepSwitchOn(false);
    }
  }, [arrowPosition, redSlices, yellowSlices, blueSlices]);

  // score / phase changes
  useEffect(() => {
    setCrySoundIndex(Math.floor(Math.random() * 4));
    setLaughSoundIndex(Math.floor(Math.random() * 4));
    setBabbleSoundIndex(Math.floor(Math.random() * 4));

    if (score >= 5 && phase === 'play') {
      setPhase('video');
      setPanCommand({ yawDeg: -100, pitchDeg: -50, durationSec: 1.25 });
      setLookAtCommand({ target: [0, 1, 0], durationSec: 1.25 });
    }
  }, [score, phase]);

  // update sleep switch based on arrow
  useEffect(() => {
    const isInRed = redSlices.some(([start, end]) => arrowPosition >= start && arrowPosition <= end);
    const isInYellow = yellowSlices.some(([start, end]) => arrowPosition >= start && arrowPosition <= end);
    const isInBlue = blueSlices.some(([start, end]) => arrowPosition >= start && arrowPosition <= end);

    if (isInRed || isInYellow) setIsSleepSwitchOn(true);
    if (isInBlue) setIsSleepSwitchOn(false);
  }, [arrowPosition, redSlices, yellowSlices, blueSlices]);

  // shadows on models
  useEffect(() => {
    cribModel.scene.traverse((obj) => {
      if (obj.isMesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });

    babyMonitor.scene.traverse((obj) => {
      if (obj.isMesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
  }, [cribModel, babyMonitor]);

  return (
    <div className={`page ${transitionClass}`}>
      <div className="mobile-life-bar">
        {phase === 'play' && (
          <S1LifeMeter
            score={score}
            setArrowPosition={setArrowPosition}
            isSleepSwitchOn={isSleepSwitchOn}
          />
        )}

        <div className="canvas-container">
          <Canvas shadows>
            <LevelCamera
              target={[0, 0.6, 0]}
              yawDeg={-20}
              pitchDeg={0}
              radius={3}
              fov={80}
              enabled={phase === 'play'}
              // to "Freeze" movement during the night add && isSleepSwitchOn as shown here
              // enabled={phase === 'play' && isSleepSwitchOn}
              panCommand={panCommand}
              lookAtCommand={lookAtCommand}
            />

            {/* ONE light rig only */}
            <AnimatedLights
              isNight={isInBlueSlice}
              wedgeTier={wedgeTier}
              stepSize={0.25}
              yReset={6.5}
            />

            <group ref={groupRef}>
              <primitive object={cribModel.scene} />
              <primitive
                object={babyMonitor.scene}
                scale={0.49}
                position={[1.55, 0.07, -1.95]}
                rotation={[0, 0.25, -0.15]}
              />

              <Physics gravity={[0, -9.8, 0]} iterations={10}>
                <Blocks
                  crySound={crySounds[crySoundIndex]}
                  laughSound={laughSounds[laughSoundIndex]}
                  babbleSound={babbleSounds[babbleSoundIndex]}
                  onBlockClick={handleBlockClick}
                />

                <RigidBody type="fixed" position={[0, -0.4, 0]}>
                  <mesh receiveShadow>
                    <boxGeometry args={[10, 1, 10]} />
                    <meshStandardMaterial opacity={0} transparent />
                  </mesh>
                </RigidBody>

                {['left', 'right', 'front', 'back'].map((side, index) => (
                  <RigidBody
                    key={index}
                    type="fixed"
                    position={
                      side === 'left' ? [-4.9, 0, 0] :
                      side === 'right' ? [4.9, 0, 0] :
                      side === 'front' ? [2.25, 0, 3.5] :
                      [0, 0, -3.65]
                    }
                  >
                    <mesh>
                      <boxGeometry args={side === 'left' || side === 'right' ? [5, 10, 10] : [10, 10, 1]} />
                      <meshStandardMaterial opacity={0} transparent />
                    </mesh>
                  </RigidBody>
                ))}

                <RigidBody type="fixed" position={[0, 3.75, 0]}>
                  <mesh receiveShadow>
                    <boxGeometry args={[10, 1, 10]} />
                    <meshStandardMaterial opacity={0} transparent />
                  </mesh>
                </RigidBody>
              </Physics>
            </group>

            <SlideDown
              targetRef={groupRef}
              start={phase === 'video' && slideStart}
              duration={0.5}
              distance={-7}
            />

            {phase === "play" && (
              <Background360Night
                daySrc={DAY_360}
                nightSrc={NIGHT_360}
                nightOn={isInBlueSlice}
              />
            )}

            {phase === 'video' && (
              <VideoSky
                src={panoMp4}
                playing={true}
                onEnded={handleNavigate}
                transitionClass
              />
            )}
          </Canvas>
        </div>
      </div>
    </div>
  );
}